'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Phone, ChevronDown, Copy, Check } from 'lucide-react';
import type { AuthState, UserRole } from './AuthPage';
import { useLanguage } from '@/contexts/LanguageContext';
import { checkRateLimit, sanitizeInput } from '@/lib/security';

interface PhoneLoginFormProps {
  authState: AuthState;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState>>;
}

interface PhoneFormData {
  phone: string;
}

const countryCodes = [
  { code: '+41', flag: '🇨🇭', name: 'CH' },
  { code: '+44', flag: '🇬🇧', name: 'GB' },
  { code: '+1', flag: '🇺🇸', name: 'US' },
  { code: '+49', flag: '🇩🇪', name: 'DE' },
  { code: '+33', flag: '🇫🇷', name: 'FR' },
  { code: '+234', flag: '🇳🇬', name: 'NG' },
  { code: '+91', flag: '🇮🇳', name: 'IN' },
  { code: '+27', flag: '🇿🇦', name: 'ZA' },
];

const demoCredentials = [
  { role: 'Customer' as UserRole, phone: '7700900123', code: '+44', display: '+44 7700 900 123' },
  { role: 'Staff' as UserRole, phone: '7700900456', code: '+44', display: '+44 7700 900 456' },
];

export default function PhoneLoginForm({ authState, setAuthState }: PhoneLoginFormProps) {
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitError, setRateLimitError] = useState('');
  const { t } = useLanguage();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<PhoneFormData>();

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleDemoFill = (cred: (typeof demoCredentials)[0]) => {
    setValue('phone', cred.phone);
    setSelectedCountry(countryCodes.find((c) => c.code === cred.code) ?? countryCodes[0]);
    setAuthState((prev) => ({ ...prev, role: cred.role }));
  };

  const onSubmit = async (data: PhoneFormData) => {
    // Rate limit: max 5 login attempts per 5 minutes
    const rl = checkRateLimit('phone-login', 5, 5 * 60 * 1000, 15 * 60 * 1000);
    if (!rl.allowed) {
      const mins = rl.lockedUntilMs ? Math.ceil((rl.lockedUntilMs - Date.now()) / 60000) : 15;
      setRateLimitError(`Too many attempts. Please try again in ${mins} minute(s).`);
      return;
    }
    setRateLimitError('');
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setAuthState((prev) => ({
      ...prev,
      phone: `${selectedCountry.code}${sanitizeInput(data.phone)}`,
      view: 'otp',
    }));
    setIsLoading(false);
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1.5">{t.auth.welcomeBack}</h2>
        <p className="text-sm text-muted-foreground">
          {t.auth.enterPhone}
        </p>
      </div>

      {/* Role toggle */}
      <div className="flex p-1 bg-muted rounded-xl mb-7 border border-border">
        {(['customer', 'staff'] as UserRole[]).map((role) => (
          <button
            key={`role-${role}`}
            type="button"
            onClick={() => setAuthState((prev) => ({ ...prev, role }))}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 capitalize ${
              authState.role === role
                ? 'bg-card text-foreground shadow-sm border border-border'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {role === 'customer' ? t.auth.customer : t.auth.bankStaff}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Phone input */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t.auth.mobileNumber}
          </label>
          <div className="flex gap-2">
            {/* Country code selector */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCountryPicker(!showCountryPicker)}
                className="h-full flex items-center gap-1.5 px-3 bg-input border border-border rounded-lg text-sm font-medium text-foreground hover:border-primary/40 transition-colors whitespace-nowrap"
              >
                <span>{selectedCountry.flag}</span>
                <span>{selectedCountry.code}</span>
                <ChevronDown size={13} className="text-muted-foreground" />
              </button>
              {showCountryPicker && (
                <div className="absolute top-full left-0 mt-1 w-44 card-elevated z-20 py-1 slide-up">
                  {countryCodes.map((c) => (
                    <button
                      key={`cc-${c.code}`}
                      type="button"
                      onClick={() => { setSelectedCountry(c); setShowCountryPicker(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <span>{c.flag}</span>
                      <span className="font-medium">{c.name}</span>
                      <span className="text-muted-foreground ml-auto">{c.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1">
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="7700 900 123"
                className={`input-field ${errors.phone ? 'error' : ''}`}
                {...register('phone', {
                  required: t.auth.phoneRequired,
                  pattern: {
                    value: /^[0-9\s]{7,15}$/,
                    message: t.auth.phoneInvalid,
                  },
                })}
              />
            </div>
          </div>
          {errors.phone && (
            <p className="text-xs text-negative mt-1.5 flex items-center gap-1">
              <Phone size={11} />
              {errors.phone.message}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1.5">
            {t.auth.weWillSend}
          </p>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t.auth.sendingCode}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Phone size={16} />
              {t.auth.sendCode}
            </span>
          )}
        </button>
        {rateLimitError && (
          <p className="text-xs text-negative text-center bg-negative/8 border border-negative/20 rounded-lg px-3 py-2">
            {rateLimitError}
          </p>
        )}
      </form>

      {/* Register link */}
      <p className="text-sm text-center text-muted-foreground mt-6">
        {t.auth.newToSifiBank}{' '}
        <button
          onClick={() => setAuthState((prev) => ({ ...prev, view: 'register' }))}
          className="text-primary font-semibold hover:underline"
        >
          {t.auth.openAccount}
        </button>
      </p>

      {/* Demo credentials */}
      <div className="mt-8 p-4 rounded-xl bg-muted border border-border">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
          {t.auth.demoAccounts}
        </p>
        <div className="space-y-2">
          {demoCredentials.map((cred) => (
            <div
              key={`demo-${cred.role}`}
              className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-card border border-border"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    cred.role === 'customer' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'
                  }`}
                >
                  {cred.role.toUpperCase()}
                </span>
                <span className="text-xs font-mono-data text-foreground">{cred.display}</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => handleCopy(cred.display, `copy-${cred.role}`)}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1"
                  title="Copy phone number"
                >
                  {copiedField === `copy-${cred.role}` ? (
                    <Check size={13} className="text-positive" />
                  ) : (
                    <Copy size={13} />
                  )}
                </button>
                <button
                  onClick={() => handleDemoFill(cred)}
                  className="text-[11px] font-semibold text-primary hover:underline px-1"
                >
                  Use
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground mt-2.5">{t.auth.demoOtp}</p>
      </div>
    </div>
  );
}